/* eslint-disable no-param-reassign */
import Vue from 'vue'
import PadletApi from 'surface/padlet_api'
import buildPost from 'surface/build_post'
import { Module, ActionContext } from 'vuex'
import { PostState, RootState } from 'surface/xstore/types'
import { appInteractionStarted } from 'bits/app_variant'
import { sortPostsByDate, sortPostsBySortIndex } from 'surface/sorter'
import {
  deletePost,
  deleteAllPosts,
  savePost,
  customSavePost,
  CustomSavePostRequest,
  generateClientId,
  isValidClientId,
  isNew,
  isEmpty,
  isNonEmpty,
} from 'surface/xstore/helpers/post'
import {
  Post,
  Cid,
  Id,
  PostAttributes,
} from 'types'

import {
  isNew as isSectionNew,
  Section,
} from 'surface/xstore/helpers/section'

import { captureException } from 'bits/error_tracker'
import window from 'bits/global'

const postModule: Module<PostState, RootState> = {
  namespaced: true,
  state: {
    postBeingEditedCid: null,
    postEntitiesByCid: {},
    pendingPostEntitiesByCid: {},
    postsFetched: false,
    chatAttachment: null,
    newestPostCid: null,
  },
  getters: {
    // State
    postBeingEditedCid: state => state.postBeingEditedCid,
    postEntitiesByCid: state => state.postEntitiesByCid,
    pendingPostEntitiesByCid: state => state.pendingPostEntitiesByCid,
    postsFetched: state => state.postsFetched,
    chatAttachment: state => state.chatAttachment,
    newestPostCid: state => state.newestPostCid,
    // Computed from state
    getPostByCid: (state): Function => {
      return (cid: Cid): Post|null => state.postEntitiesByCid[cid]
    },
    getPostByServerId: (_, getters): Function => {
      return (id: Id): Post|null => {
        return getters.postsArray.find((p: Post): boolean => p.id === id)
      }
    },
    postsArray: (state, getters): Post[] => {
      return getters.postCids.map((cid: Cid): object => state.postEntitiesByCid[cid])
    },
    nonEmptyPostsArray: (state, getters): Post[] => {
      return getters.postsArray.filter(isNonEmpty)
    },
    postBeingEdited: (state): Post|null => {
      return state.postBeingEditedCid ? state.postEntitiesByCid[state.postBeingEditedCid] : null
    },
    postCids: (state): Cid[] => Object.keys(state.postEntitiesByCid),
    pendingPosts: (state, getters): Post[] => {
      return getters.pendingPostCids.map((cid: Cid): object => state.pendingPostEntitiesByCid[cid])
    },
    pendingPostCids: (state): Cid[] => Object.keys(state.pendingPostEntitiesByCid),
    postCidsBySection: (state, getters, _, rootGetters): Record<string, string> => {
      if (rootGetters.isShelf) {
        const postCidsBySection = {}
        getters.postCids.forEach((pid: string): void => {
          const p = state.postEntitiesByCid[pid]
          if (!p.wall_section_id) return
          postCidsBySection[p.wall_section_id] = postCidsBySection[p.wall_section_id] || []
          postCidsBySection[p.wall_section_id].push(pid)
        })
        return postCidsBySection
      }
      return {}
    },
    sortedPosts (state, getters, rootState, rootGetters): Post[] {
      const postsToShow = rootState.inlinePostEdit ? getters.postsArray : getters.nonEmptyPostsArray
      if (rootGetters.isBackchannel) return sortPostsByDate(postsToShow)
      if (rootGetters.isTimeline) return sortPostsBySortIndex(postsToShow).reverse()
      return sortPostsBySortIndex(postsToShow)
    },
  },
  mutations: {
    FETCH_POSTS_SUCCESS (state, posts: Post[]): void {
      if (posts) {
        //  >>>>
        //  Ensure UI consistency if a post is currently being edited and a fetch
        //  posts call is made.
        let currentlyEditingPostId: Id | undefined
        let currentlyEditingPostCid: Cid | null = null
        if (state.postBeingEditedCid) {
          // There's a post currently being edited.
          // Check to see if the post has already been assigned an ID (i.e. it's not new). If it's an existing post, then
          // we should assign the existing client ID for this post to its updated copy that was just fetched.
          // This keeps the UI  consistent to before this mutation was called -- the post will still
          // be in "edit" mode, and because the "key" attribute to the post component remains the same, the
          // cursor would not have moved as well.
          const postBeingEdited = state.postEntitiesByCid[state.postBeingEditedCid]
          if (!isNew(postBeingEdited)) {
            currentlyEditingPostCid = state.postBeingEditedCid
            currentlyEditingPostId = postBeingEdited.id
          }
        }
        // <<<<

        const newPostEntities = {}
        posts.forEach((post: Post): void => {
          let cid = generateClientId(post)
          // Ensure consistency of the Cid for the post being edited before and
          // after fetch posts success -- keeps the UI consistent.
          if (post.id && currentlyEditingPostCid && post.id === currentlyEditingPostId) {
            cid = currentlyEditingPostCid
          }
          newPostEntities[cid] = { ...post, cid }
        })

        // Preserve a new post being edited
        if (state.postBeingEditedCid &&
          state.postEntitiesByCid[state.postBeingEditedCid] &&
          isNew(state.postEntitiesByCid[state.postBeingEditedCid]) &&
          !newPostEntities[state.postBeingEditedCid]
        ) {
          newPostEntities[state.postBeingEditedCid] = state.postEntitiesByCid[state.postBeingEditedCid]
        }

        state.postEntitiesByCid = newPostEntities // Trigger state change once
        state.pendingPostEntitiesByCid = {}
      }
      state.postsFetched = true
    },
    // Updates pending post with the given attributes,
    // e.g. assign a table cell to dragged and dropped files before they are uploaded.
    UPDATE_PENDING_POST (state, postAttributes): void {
      const post = state.pendingPostEntitiesByCid[postAttributes.cid]
      if (!post) return
      const posts = {
        ...state.pendingPostEntitiesByCid,
        [post.cid]: { ...post, ...postAttributes },
      }
      state.pendingPostEntitiesByCid = posts
    },
    ADD_PENDING_POST (state, pendingPost): void {
      const cid = generateClientId(pendingPost)
      state.pendingPostEntitiesByCid = {
        ...state.pendingPostEntitiesByCid,
        [cid]: {
          ...pendingPost,
          cid,
        },
      }
    },
    REMOVE_PENDING_POST (state, { cid }): void {
      const newPendingPostEntities = { ...state.pendingPostEntitiesByCid }
      delete newPendingPostEntities[cid]
      state.pendingPostEntitiesByCid = newPendingPostEntities
    },
    REMOVE_PENDING_POSTS (state, posts): void {
      const newPendingPostEntities = { ...state.pendingPostEntitiesByCid }
      posts.forEach(({ id }): void => {
        delete newPendingPostEntities[id]
      })
      state.pendingPostEntitiesByCid = newPendingPostEntities
    },
    REMOVE_ALL_PENDING_POSTS_WITHOUT_SECTION (state): void {
      const newPendingPostEntities = { ...state.pendingPostEntitiesByCid }
      const ppids = Object.keys(state.pendingPostEntitiesByCid)
      ppids.forEach((ppid): void => {
        const pendingPost = newPendingPostEntities[ppid]
        if (!pendingPost.wall_section_id) {
          delete newPendingPostEntities[ppid]
        }
      })
      state.pendingPostEntitiesByCid = newPendingPostEntities
    },
    REJECT_POSTS_WITH_FILE_SIZE_EXCEEDED (state, file): void {
      if (file) {
        const newPendingPostEntities = { ...state.pendingPostEntitiesByCid }
        const ppids = Object.keys(state.pendingPostEntitiesByCid)
        ppids.forEach((ppid): void => {
          const pendingPost = newPendingPostEntities[ppid]
          if (pendingPost.file === file) {
            delete newPendingPostEntities[ppid]
          }
        })
        state.pendingPostEntitiesByCid = newPendingPostEntities
      }
    },
    START_NEW_POST (state, newPostAttributes): void {
      const cid = generateClientId(newPostAttributes)
      state.postBeingEditedCid = cid
      state.postEntitiesByCid = {
        ...state.postEntitiesByCid,
        [cid]: {
          ...newPostAttributes,
          cid,
        },
      }
    },
    CREATED_POST (state, post): void {
      const postCreated = post
      // post is not on client yet
      if (!postCreated.cid) {
        const cid = generateClientId(postCreated)
        postCreated.cid = cid
      }
      Vue.set(state.postEntitiesByCid, postCreated.cid, postCreated)
    },
    CREATED_POST_SUCCESS (state, post): void {
      Vue.set(state.postEntitiesByCid, post.cid, post)
    },
    CREATED_POST_ERROR (state, post): void {
      // if (post.cid) {
      //   Vue.delete(state.postEntitiesByCid, post.cid)
      // }
    },
    MARK_NEWEST_POST (state, cid): void {
      state.newestPostCid = cid
    },
    DELETED_POST (state, post): void {
      let postDeletedCid = post.cid
      if (!postDeletedCid) {
        postDeletedCid = Object.keys(state.postEntitiesByCid).find((cid): boolean => {
          return state.postEntitiesByCid[cid].id === post.id
        })
      }
      if (postDeletedCid) {
        if (postDeletedCid === state.postBeingEditedCid) {
          state.postBeingEditedCid = null
        }
        Vue.delete(state.postEntitiesByCid, postDeletedCid)
      }
    },
    DELETED_POST_SUCCESS (): void {},
    DELETED_POST_ERROR (state, post): void {
      // Vue.set(state.postEntitiesByCid, post.cid, post)
    },
    UPDATED_POST (state, post): void {
      let postUpdatedId = post.cid
      if (!postUpdatedId) {
        postUpdatedId = Object.keys(state.postEntitiesByCid).find((cid): boolean => {
          return state.postEntitiesByCid[cid].id === post.id
        })
        post.cid = postUpdatedId
      }
      Vue.set(state.postEntitiesByCid, postUpdatedId, post)
    },
    UPDATED_POST_SUCCESS (): void {},
    UPDATED_POST_ERROR (state, post): void {
      // TODO: recover to old post
      Vue.set(state.postEntitiesByCid, post.cid, post)
    },
    REMOVE_POST_BEING_EDITED (state): void {
      if (!state.postBeingEditedCid) return
      const newPostEntities = { ...state.postEntitiesByCid }
      delete newPostEntities[state.postBeingEditedCid]
      state.postBeingEditedCid = null
      state.postEntitiesByCid = newPostEntities
    },
    START_EDITING_POST (state, postCid): void {
      state.postBeingEditedCid = postCid
    },
    STOP_EDITING_POST (state): void {
      state.postBeingEditedCid = null
    },
    UPDATE_CID_WITH_SERVER_ID (state, oldCid): void {
      const post = state.postEntitiesByCid[oldCid]
      if (!post || !post.id) return

      const cid = generateClientId(post)
      if (cid === post.cid) return

      const postEntitiesByCid = { ...state.postEntitiesByCid, [cid]: { ...post, cid } }
      delete postEntitiesByCid[oldCid]
      state.postEntitiesByCid = postEntitiesByCid
    },
    CHANGE_POST_TEXT (state, { postCid, attributes }: {postCid: Cid, attributes: PostAttributes}): void {
      let postToBeSaved: Post = state.postEntitiesByCid[postCid]
      postToBeSaved = Object.assign(postToBeSaved, attributes)
      Vue.set(state.postEntitiesByCid, postCid, postToBeSaved)
    },
    SORTED_POST (state, { postCid, sortIndex, sectionId }): void {
      if (sortIndex) {
        Vue.set(state.postEntitiesByCid[postCid], 'sort_index', sortIndex)
      }
      if (sectionId) {
        Vue.set(state.postEntitiesByCid[postCid], 'wall_section_id', sectionId)
      }
    },
    MOVED_POST (state, { postCid, position }): void {
      if (position.left) {
        Vue.set(state.postEntitiesByCid[postCid], 'left', position.left)
      }
      if (position.top) {
        Vue.set(state.postEntitiesByCid[postCid], 'top', position.top)
      }
    },
    RESIZED_POST (state, { postCid, size }): void {
      if (size && size.width) {
        Vue.set(state.postEntitiesByCid[postCid], 'width', size.width)
      }
    },
    APPROVE_POST (state, { postCid }): void {
      Vue.set(state.postEntitiesByCid[postCid], 'published', true)
    },
    REMOVE_POST_COLOR (state, { postCid }): void {
      Vue.set(state.postEntitiesByCid[postCid], 'color', null)
    },
    CHANGE_POST_COLOR (state, { postCid, color }): void {
      Vue.set(state.postEntitiesByCid[postCid], 'color', color)
    },
    SAVE_ATTACHMENT_CHAT (state, attachment): void {
      if (!state.postBeingEditedCid) {
        state.chatAttachment = attachment
      } else {
        Vue.set(state.postEntitiesByCid[state.postBeingEditedCid], 'attachment', attachment)
      }
    },
    REMOVE_ATTACHMENT_CHAT (state): void {
      state.chatAttachment = null
    },
    SAVE_ATTACHMENT (state, attachment): void {
      if (state.postBeingEditedCid) {
        Vue.set(state.postEntitiesByCid[state.postBeingEditedCid], 'attachment', attachment)
      }
    },
    REMOVE_ATTACHMENT (state, postCid): void {
      if (postCid) {
        Vue.set(state.postEntitiesByCid[postCid], 'attachment', null)
      } else if (state.postBeingEditedCid) {
        Vue.set(state.postEntitiesByCid[state.postBeingEditedCid], 'attachment', null)
      }
    },
    SAVE_FILE (state, file): void {
      if (state.postBeingEditedCid) {
        Vue.set(state.postEntitiesByCid[state.postBeingEditedCid], 'file', file)
      }
    },
    CHANGE_ATTACHMENT (state, { postCid, attributes }): void {
      Vue.set(state.postEntitiesByCid[postCid || attributes.cid], 'attachment', attributes.attachment)
    },
  },
  actions: {
    /* ------------
     * CR-D
     * ------------ */
    async fetchPosts ({ dispatch, rootState }): Promise<any> {
      try {
        const posts = await PadletApi.Wish.readAll({ wall: { id: rootState.wall.id } })
        dispatch('fetchPostsSuccess', { posts })
      } catch (e) {
        captureException(e)
      }
    },
    fetchPostsSuccess ({ commit }, { posts }): void {
      commit('FETCH_POSTS_SUCCESS', posts)
    },
    /**
     * Start a new post (either by the pink button or double-clicking)
     * Similar to createPost, but createPost creates the post and submits it immediately
     * startNewPost will creates the post locally with empty content and set it as being
     * edited.
     * @param context - vuex action context
     * @param payload - vuex payload
     * @param payload.attributes - attributes of the post to be created
     */
    startNewPost ({ rootState, rootGetters, commit, dispatch }, { attributes } = { attributes: { wall_section_id: null, file: null } }): void {
      if (!rootState.cookiesEnabled) {
        dispatch('showCookieNotification', null, { root: true })
        return
      }
      let newPostAttributes = { ...attributes }
      if (rootGetters.isShelf &&
        !newPostAttributes.wall_section_id) {
        // Missing section id
        return
      }
      dispatch('removeEmptyPostBeingEdited')
      newPostAttributes = buildPost(newPostAttributes, { state: rootState, getters: rootGetters })
      commit('START_NEW_POST', newPostAttributes)
      appInteractionStarted()
    },
    /**
     * Create post immediately. Example scenarios: paste/drop to post and in composer mode
     * @param context - vuex action context
     * @param payload - vuex payload
     * @param payload.attributes - attributes of the post to be created
     */
    createPost (context: ActionContext<PostState, RootState>, { attributes } = { attributes: { wall_section_id: null, file: null } }): void {
      // when a new post is to be created there are a few scenarios:
      // 1. We have all the data we need
      // 2. We have a pending file upload
      // 3. We have a pending section id
      // 4. Both 2 and 3

      // if the post is missing a section and there are more than one sections,
      // add post to the pending list
      // (scenario 3 or 4)
      const { commit, rootState, rootGetters, dispatch } = context
      if (!rootState.cookiesEnabled) {
        dispatch('showCookieNotification', null, { root: true })
        return
      }

      let newPostAttributes = attributes
      const usableSections = rootGetters['section/sections'].filter((x: Section): boolean => !isSectionNew(x))

      if (rootGetters.isShelf &&
        !newPostAttributes.wall_section_id && usableSections.length !== 1) {
        commit('ADD_PENDING_POST', newPostAttributes)
      } else {
        // if there is only one section, use that section
        if (rootGetters.isShelf &&
          !newPostAttributes.wall_section_id && usableSections.length === 1) {
          newPostAttributes = {
            ...newPostAttributes,
            wall_section_id: usableSections[0].id,
          }
        }
        // if there is a pending file, add to pending
        // (scenario 2)
        if ('file' in newPostAttributes && newPostAttributes.file) {
          commit('ADD_PENDING_POST', newPostAttributes)
        // otherwise, create a new post
        // (scenario 1)
        } else {
          newPostAttributes = buildPost(newPostAttributes, {
            state: rootState, getters: rootGetters,
          })
          newPostAttributes.cid = generateClientId(newPostAttributes)
          savePost(context, newPostAttributes)
          commit('MARK_NEWEST_POST', newPostAttributes.cid)
          // state.posts.create(newPostAttributes)
        }
      }
    },
    removeEmptyPostBeingEdited ({ commit, dispatch, getters }): void {
      if (getters.postBeingEdited && isEmpty(getters.postBeingEdited)) {
        if (!isNew(getters.postBeingEdited)) {
          deletePost({ commit, dispatch }, getters.postBeingEdited)
        }
        commit('REMOVE_POST_BEING_EDITED')
      }
    },
    /**
     * Used in composer mode (post being edited in the composer instead inside the
     * post itself real-timely)
     * The action is used when user press save after finish editing
     * @param context - vuex action context
     * @param payload - vuex payload
     * @param payload.attributes - attributes of the post to be created
     */
    savePost (context: ActionContext<PostState, RootState>,
      { attributes }: {attributes: Post|PostAttributes}): void {
      const {
        commit, dispatch, rootState, rootGetters,
      } = context
      let postToBeSaved: Post|PostAttributes
      if (!attributes.cid || !attributes.id || !attributes.wall_id) {
        // if new post -> post will be created
        postToBeSaved = buildPost(attributes, { state: rootState, getters: rootGetters })
        postToBeSaved.cid = generateClientId(postToBeSaved)
        savePost(context, postToBeSaved)
        commit('MARK_NEWEST_POST', postToBeSaved.cid)
      } else { // post is being edited ->  post will be updated
        postToBeSaved = attributes
        savePost(context, postToBeSaved)
      }
      dispatch('stopEditingPost')
    },
    /* ------------
     * POST MODIFICATION OPERATIONS
     * ------------ */
    startEditingPost ({ rootState, commit, dispatch }, { postCid }): void {
      if (!rootState.cookiesEnabled) {
        dispatch('showCookieNotification', null, { root: true })
        return
      }
      dispatch('stopEditingPost')
      commit('START_EDITING_POST', postCid)
    },
    stopEditingPost ({ commit, dispatch, state }): void {
      dispatch('removeEmptyPostBeingEdited')
      if (!state.postBeingEditedCid) return
      commit('UPDATE_CID_WITH_SERVER_ID', state.postBeingEditedCid)
      commit('STOP_EDITING_POST')
    },
    syncPostBeingEditedCidForMap ({ dispatch, state, rootGetters }, newlyCreatedPost: Post): void {
      // We need to do this so that on mobile, the postBeingEditedCid is updated
      // to the cid of the post that is just created and saved.
      // TODO: Make mobile map native, then remove this.
      if (rootGetters.isMap) {
        if (newlyCreatedPost.cid === state.postBeingEditedCid) {
          dispatch('startEditingPost', { postCid: generateClientId(newlyCreatedPost) })
        }
      }
    },
    changePostText (context, { postCid, attributes }): void {
      const { state, commit } = context
      commit('CHANGE_POST_TEXT', { postCid, attributes })
      const postToBeSaved = state.postEntitiesByCid[postCid]
      if (isEmpty(postToBeSaved)) {
        // Do nothing
        // We do not save empty post, but when the edit is over,
        // stopEditingPost will handle the empty post and delete it
      } else {
        savePost(context, postToBeSaved)
      }
    },
    completePostUpload (context, { file, status }): void {
      const {
        commit, getters, rootState, rootGetters,
      } = context
      let completedPostAttributes = getters.pendingPosts.find((post): boolean =>  post.file === file)
      commit('REMOVE_PENDING_POST', completedPostAttributes)
      if (status !== 'error' && status !== 'cancel') {
        completedPostAttributes = {
          ...completedPostAttributes,
          attachment: status,
        }
        completedPostAttributes = buildPost(completedPostAttributes, {
          state: rootState,
          getters: rootGetters,
        })
        savePost(context, completedPostAttributes)
      }
    },
    updateAfterPostCreated (context, { postCid }): void {
      const post = context.state.postEntitiesByCid[postCid]
      if (!post) { return }
      /*
        One of the following state transitions will occur:
        - New >> Creating >> UpdateWaiting
        - Creating >> UpdateWaiting >> UpdateWaiting
        - UpdateWaiting >> UpdateWaiting >> UpdateWaiting
        - Updating >> UpdateWaiting >> UpdateWaiting
        - Created >> DebouncingUpdate >> DebouncingUpdate
        - DebouncingUpdate >> DebouncingUpdate >> DebouncingUpdate
      */
      savePost(context, post)
      savePost(context, post)
    },
    movedPost ({ commit, dispatch }, { postCid, position }): void {
      commit('MOVED_POST', { postCid, position })
      dispatch('updateAfterPostCreated', { postCid })
    },
    sortedPost ({ commit, dispatch }, { postCid, sortIndex, sectionId }): void {
      commit('SORTED_POST', { postCid, sortIndex, sectionId })
      dispatch('updateAfterPostCreated', { postCid })
    },
    resizedPost ({ commit, dispatch }, { postCid, size }): void {
      commit('RESIZED_POST', { postCid, size })
      dispatch('updateAfterPostCreated', { postCid })
    },
    deletePost ({
      state, commit, getters, dispatch,
    }, { post, postCid }): void {
      function getDeletingPostCid (): string {
        let cid = postCid || (post && post.cid)
        if (!cid || !isValidClientId(cid)) {
          cid = getters.getPostByServerId((post && post.id) || postCid).cid
        }
        return cid
      }

      let deletingPostCid = getDeletingPostCid()
      let deletingPost = post || state.postEntitiesByCid[deletingPostCid]

      if (deletingPostCid === state.postBeingEditedCid) {
        dispatch('stopEditingPost')
        if (isNew(deletingPost)) return
        // If the post is new, cid would have changed during stopEditingPost
        deletingPostCid = getDeletingPostCid()
        deletingPost = state.postEntitiesByCid[deletingPostCid]
      }

      dispatch('removeConnectionsForPost', { postId: deletingPost.id }, { root: true })
      deletePost({ commit, dispatch }, deletingPost)
    },
    approvePost ({ state, commit }, { postCid }): void {
      commit('APPROVE_POST', { postCid })
      PadletApi.Wish.approve({ wishId: state.postEntitiesByCid[postCid].id })
    },
    removePostColor ({ commit, dispatch }, { postCid }): void {
      commit('REMOVE_POST_COLOR', { postCid })
      dispatch('updateAfterPostCreated', { postCid })
    },
    changePostColor ({ commit, dispatch }, { postCid, newBgColor }): void {
      commit('CHANGE_POST_COLOR', { postCid, color: newBgColor })
      dispatch('updateAfterPostCreated', { postCid })
    },
    /**
     * Does not trigger delete post on server with PadletApi
     */
    removePost ({ commit }, { id, cid }): void {
      commit('DELETED_POST', { id, cid })
    },
    addPost ({ commit }, { postAttributes }): void {
      commit('CREATED_POST', postAttributes)
    },
    /* ------------
     * REMOTE
     * ------------ */
    newPostRemote ({ commit, rootState, rootGetters }, post): void {
      if (post.published || rootGetters.canIModerate ||
        (post.author_id === rootState.user.id && rootState.user.id !== null)) {
        commit('CREATED_POST', post)
      }
    },
    editPostRemote ({
      commit, state, getters, rootState, rootGetters,
    }, postAttributes): void {
      const currentPostCid = getters.postCids.find(
        (cid): boolean => state.postEntitiesByCid[cid].id === postAttributes.id
      )
      const currentPost = state.postEntitiesByCid[currentPostCid]
      if (currentPost) {
        // real time can be out of sync sometimes.
        // here we make sure we are not updating a new copy with a stale one
        if (!currentPost.updated_at) return
        if (!postAttributes.updated_at) return
        if (currentPost.updated_at <= postAttributes.updated_at) {
          if (currentPost.published || rootGetters.canIModerate ||
            (currentPost.author_id === rootState.user.id && rootState.user.id !== null)) {
            commit('UPDATED_POST', { ...postAttributes, cid: currentPostCid })
          } else {
            commit('DELETED_POST', { ...postAttributes, cid: currentPostCid })
          }
        }
      }
    },
    deletePostRemote ({ commit }, post): void {
      commit('DELETED_POST', post)
    },
    clearAllPosts ({ getters, commit }): void {
      commit('HIDE_CLEAR_ALL_DIALOG', null, { root: true })
      deleteAllPosts({ commit }, getters.postsArray)
    },

    saveContent (context, { content }): void {
      const { commit, dispatch, state, rootGetters } = context
      dispatch('contentPicker/hideContentPickerPanel', null, { root: true })
      if (content.link) {
        if (rootGetters.format === 'chat') {
          commit('SAVE_ATTACHMENT_CHAT', content.link)
        } else {
          commit('SAVE_ATTACHMENT', content.link)
          const post = state.postBeingEditedCid && state.postEntitiesByCid[state.postBeingEditedCid]
          if (post) {
            if (isEmpty(post)) {
              // do nothing
            } else {
              savePost(context, post)
            }
          }
        }
      } else {
        commit('SAVE_FILE', content.file)
      }
    },
    removePostAttachment ({ state, commit, dispatch, rootGetters }, { postCid } = { postCid: null }): void {
      commit('REMOVE_ATTACHMENT', postCid)
      const post = state.postEntitiesByCid[postCid || state.postBeingEditedCid]
      if (rootGetters.isBackchannel) {
        commit('REMOVE_ATTACHMENT_CHAT')
      } else if (post && isEmpty(post)) {
        // do nothing
      } else {
        dispatch('updateAfterPostCreated', { postCid: postCid || state.postBeingEditedCid })
      }
    },
    changePostAttachment ({ commit, dispatch }, { postCid, attributes }): void {
      commit('CHANGE_ATTACHMENT', { postCid, attributes })
      dispatch('updateAfterPostCreated', { postCid })
    },
    pickSection (context, { sectionId }): void {
      const {
        getters, rootState, rootGetters, commit, dispatch,
      } = context
      const postsToRemoveFromPending: any[] = []
      getters.pendingPosts.forEach((postAttributes): void => {
        // if a pending post has a wall_section_id, do nothing
        // it is likely waiting for something else (a file to complete uploading)
        if (!postAttributes.wall_section_id) {
          let finalPostAttributes
          postAttributes.wall_section_id = sectionId
          // if the post has a pending file upload, do nothing
          if (postAttributes.file) {
            // do nothing
          // if the post is blank, start a new post
          } else if (!postAttributes.subject &&
            !postAttributes.body &&
            !postAttributes.attachment) {
            finalPostAttributes = buildPost(postAttributes,
              { state: rootState, getters: rootGetters })
            dispatch('stopEditingPost')
            commit('START_NEW_POST', finalPostAttributes)
            postsToRemoveFromPending.push(postAttributes)
          // otherwise, looks like the post has everything we need, create a new one
          } else {
            finalPostAttributes = buildPost(postAttributes,
              { state: rootState, getters: rootGetters })
            finalPostAttributes.cid = generateClientId(finalPostAttributes)
            savePost(context, finalPostAttributes)
            commit('MARK_NEWEST_POST', finalPostAttributes.cid)
            postsToRemoveFromPending.push(postAttributes)
          }
        }
      })
      // remove posts from pending list that were either added or created
      commit('REMOVE_PENDING_POSTS', postsToRemoveFromPending)
    },
    cancelPickingSection ({ rootGetters, commit }): void {
      // no section was picked, remove all pending posts that need a section
      if (rootGetters.isShelf) {
        commit('REMOVE_ALL_PENDING_POSTS_WITHOUT_SECTION')
      }
    },
    rejectPostsWithFileSizeExceeded ({ commit }, file): void {
      commit('REJECT_POSTS_WITH_FILE_SIZE_EXCEEDED', file)
    },
    updatePendingPost ({ commit }, postAttributes: PostAttributes): void {
      commit('UPDATE_PENDING_POST', postAttributes)
    },
    removePendingPost ({ commit }, post: Post): void {
      commit('REMOVE_PENDING_POST', post)
    },
    createdPostSuccess ({ commit, dispatch, state }, post: Post): void {
      commit('CREATED_POST_SUCCESS', post)

      // Delay updating to finish all pending save actions
      // When post is created, and then immediately changed (moved/resized),
      // those operations are delayed.
      // They can be completed once the new post is synced
      // and has an id from the server for the PUT request to go through
      // This delay is to wait for the PUT requests on the old cid
      // to be called before we update the old cid to the new cid
      // TODO: prevent updating if post is being moved
      setTimeout((): void => {
        if (!state.postBeingEditedCid) commit('UPDATE_CID_WITH_SERVER_ID', post.cid)
      }, 1000)

      dispatch('syncPostBeingEditedCidForMap', post)
    },
    updatedPostSuccess ({ commit }, post: Post): void {
      commit('UPDATED_POST_SUCCESS', post)
    },
    enqueueRequest (context, { post, customRequest }: { post: Post, customRequest: CustomSavePostRequest}): Promise<any> {
      return customSavePost(context, post, customRequest)
    },
  },
}

export default postModule
